import React, { useCallback, useMemo } from 'react';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import { PrimeReactSingleSelect } from '../../../design-system-my-lib';
import type { OptionType } from '../../atoms/single-select-dropdown/single-select-dropdown';
import { ChannelCardDensity } from '../marketplace/ChannelCardMolecule';
import {
  ScopeFilterMolecule,
  ScopeFilterValue,
} from './ScopeFilterMolecule';
import './MarketplaceFilterBarMolecule.scss';

export type MarketplaceSortOption =
  | 'subscribers'
  | 'rating'
  | 'recent'
  | 'relevance';

export type SubscriptionStateFilter =
  | 'all'
  | 'subscribed'
  | 'available'
  | 'owned';

const SORT_OPTIONS: MarketplaceSortOption[] = [
  'subscribers',
  'rating',
  'recent',
  'relevance',
];

const SUB_STATES: SubscriptionStateFilter[] = [
  'all',
  'subscribed',
  'available',
  'owned',
];

export interface MarketplaceFilterBarMoleculeLabels {
  searchPlaceholder: string;
  searchAriaLabel: string;
  searchKbdHint: string;
  sortAriaLabel: string;
  sortOptions: Record<MarketplaceSortOption, string>;
  density: { roomy: string; list: string };
  densityAriaLabel?: string;
  subStateAriaLabel: string;
  subState: Record<SubscriptionStateFilter, string>;
}

export interface MarketplaceFilterBarMoleculeProps {
  search: string;
  sort: MarketplaceSortOption;
  density: ChannelCardDensity;
  scope: ScopeFilterValue;
  subState: SubscriptionStateFilter;
  onSearchChange: (value: string) => void;
  onSortChange: (value: MarketplaceSortOption) => void;
  onDensityChange: (value: ChannelCardDensity) => void;
  onScopeChange: (value: ScopeFilterValue) => void;
  onSubStateChange: (value: SubscriptionStateFilter) => void;
  labels: MarketplaceFilterBarMoleculeLabels;
  className?: string;
}

const MarketplaceFilterBarMolecule: React.FC<
  MarketplaceFilterBarMoleculeProps
> = ({
  search,
  sort,
  density,
  scope,
  subState,
  onSearchChange,
  onSortChange,
  onDensityChange,
  onScopeChange,
  onSubStateChange,
  labels,
  className = '',
}) => {
  const rootClasses = ['marketplace-marketplace-filter-bar', className]
    .filter(Boolean)
    .join(' ');

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(event.target.value);
    },
    [onSearchChange],
  );

  const sortOptions = useMemo<OptionType[]>(
    () =>
      SORT_OPTIONS.map((option) => ({
        name: labels.sortOptions[option],
        code: option,
      })),
    [labels.sortOptions],
  );

  const sortValue = useMemo<OptionType>(
    () => ({ name: labels.sortOptions[sort], code: sort }),
    [sort, labels.sortOptions],
  );

  const handleSortChange = useCallback(
    (e: { value: OptionType | null }) => {
      if (e.value) {
        // OptionType.code is `string`; narrow to the sort union. (Sonar S4325
        // flags this as redundant, but the app build's type-check requires it.)
        onSortChange(e.value.code as MarketplaceSortOption);
      }
    },
    [onSortChange],
  );

  const handleSubStateClick = useCallback(
    (value: SubscriptionStateFilter) => () => {
      onSubStateChange(value);
    },
    [onSubStateChange],
  );

  return (
    <div className={rootClasses}>
      <div className="marketplace-marketplace-filter-bar__row marketplace-marketplace-filter-bar__row--primary">
        <label className="marketplace-marketplace-filter-bar__search">
          <span className="marketplace-marketplace-filter-bar__visually-hidden">
            {labels.searchAriaLabel}
          </span>
          <i
            className="pi pi-search marketplace-marketplace-filter-bar__search-icon"
            aria-hidden="true"
          />
          <input
            type="search"
            className="marketplace-marketplace-filter-bar__search-input"
            value={search}
            placeholder={labels.searchPlaceholder}
            aria-label={labels.searchAriaLabel}
            onChange={handleSearchChange}
          />
          <kbd
            className="marketplace-marketplace-filter-bar__search-kbd"
            aria-hidden="true"
          >
            {labels.searchKbdHint}
          </kbd>
        </label>

        <div className="marketplace-marketplace-filter-bar__sort">
          <PrimeReactSingleSelect
            value={sortValue}
            options={sortOptions}
            onChange={handleSortChange}
            aria-label={labels.sortAriaLabel}
            className="marketplace-marketplace-filter-bar__sort-dropdown sort-by-dropdown"
          />
        </div>

        <SegmentedControlAtom
          mode="single"
          value={density === 'list' ? 'list' : 'roomy'}
          onChange={(v) => onDensityChange(v as ChannelCardDensity)}
          options={[
            { value: 'roomy', label: labels.density.roomy, icon: 'pi pi-th-large' },
            { value: 'list', label: labels.density.list, icon: 'pi pi-list' },
          ]}
          ariaLabel={labels.densityAriaLabel ?? 'View density'}
          size="small"
          variant="strip"
        />
      </div>

      <div className="marketplace-marketplace-filter-bar__row marketplace-marketplace-filter-bar__row--scopes">
        <ScopeFilterMolecule value={scope} onChange={onScopeChange} />

        <div
          className="marketplace-marketplace-filter-bar__sub-state"
          role="group"
          aria-label={labels.subStateAriaLabel}
        >
          {SUB_STATES.map((state) => {
            const isActive = subState === state;
            const buttonClasses = [
              'marketplace-marketplace-filter-bar__sub-state-button',
              isActive
                ? 'marketplace-marketplace-filter-bar__sub-state-button--active'
                : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={state}
                type="button"
                className={buttonClasses}
                aria-pressed={isActive}
                onClick={handleSubStateClick(state)}
              >
                {labels.subState[state]}
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
};

MarketplaceFilterBarMolecule.displayName = 'MarketplaceFilterBarMolecule';

export default MarketplaceFilterBarMolecule;
